import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';
import type { HoldBackProductionType } from '@threadheads/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';

type Cell = {
  locationId: string;
  productionType: HoldBackProductionType;
  productType: string;
  bufferUnits: number | null;
};

/**
 * Confirm step for saving Stock Hold-Back rules.
 *
 * Saving here takes stock OFF the storefront. That is the thing to confirm:
 * which SKUs go off sale and how many units stay in the warehouse.
 *
 * It no longer halts the org (BLU-878). The push guards apply today's hold-back
 * to BOTH sides of their comparison, so a settings-only change contributes zero
 * drop; the next cycle simply applies the new numbers. Only a movement in raw
 * stock can trip a guard, exactly as before the feature existed.
 *
 * The impact numbers come from a dedicated read-only procedure that calls the
 * push's own `resolvePushableQuantity`. It is deliberately NOT
 * `previewInventoryPush`: that runs change detection, which sees nothing dirty
 * for a settings-only change and would cheerfully report "no changes" right
 * before the halt.
 *
 * It runs as a MUTATION fired on open, not a query: the input is the pending
 * grid, which is not a cacheable resource, and a failed preview must be a
 * visible, retryable state rather than a cached-empty one. A failure here is a
 * hard gate — with no impact numbers the operator cannot see what goes off
 * sale, so Confirm stays disabled until a preview succeeds.
 */
export function HoldBackConfirmDialog({
  open,
  onOpenChange,
  cells,
  locations,
  isSaving,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cells: Cell[];
  locations: Array<{ id: string; name: string }>;
  isSaving: boolean;
  onConfirm: () => void | Promise<void>;
}) {
  const { t } = useTranslation('inventory');
  const [saveError, setSaveError] = useState<string | null>(null);

  const preview = trpc.inventory.previewHoldBackImpact.useMutation();
  const { mutate: runPreview, reset: resetPreview } = preview;

  // The dialog opens over a FROZEN set of pending changes (the grid behind it
  // is inert), so the preview fires once per open. Cells live in a ref rather
  // than the dep array so a parent re-render can never re-fire it mid-read.
  const cellsRef = useRef(cells);
  cellsRef.current = cells;
  const firedForOpenRef = useRef(false);
  useEffect(() => {
    if (!open) {
      firedForOpenRef.current = false;
      resetPreview();
      setSaveError(null);
      return;
    }
    if (firedForOpenRef.current || cellsRef.current.length === 0) return;
    firedForOpenRef.current = true;
    runPreview({ cells: cellsRef.current });
  }, [open, runPreview, resetPreview]);

  const impact = preview.data;
  // The server caps this list (MAX_ROWS = 50) and sets `truncated` to say so, so
  // a second client-side slice could only ever disagree with the "and N more"
  // line underneath it. Render what arrived; count the remainder from that.
  const impactRows = impact?.rows ?? [];
  const locationName = (id: string) => locations.find((l) => l.id === id)?.name ?? id;
  const skusToZero = impact?.skusToZero ?? 0;
  const previewFailed = preview.isError;
  // Idle counts as loading: the effect fires after paint, so treating idle as
  // "done" would flash a stat grid of zeros — the exact "0 held back, 0 to
  // zero" lie this dialog exists to prevent.
  const previewLoading = preview.isPending || (preview.isIdle && cells.length > 0);
  // Which SECTION of the grid this save touches. The likeliest operator mistake
  // once "T-Shirt" exists twice is typing into the wrong one, and the headline
  // "4 cells changed" cannot catch it — POD holds back BLANK garments, PAP holds
  // back finished stock, so the two are not interchangeable at any number.
  const cellsByProductionType = useMemo(
    () => ({
      pod: cells.filter((c) => c.productionType === 'POD').length,
      pap: cells.filter((c) => c.productionType === 'PAP').length,
    }),
    [cells]
  );
  // No successful preview ⇒ the operator has not seen what goes off sale ⇒
  // nothing to confirm.
  const confirmDisabled = isSaving || previewLoading || !preview.isSuccess;

  const handleConfirm = async () => {
    setSaveError(null);
    try {
      await onConfirm();
    } catch (error) {
      // The hook's destructive toast renders BEHIND this overlay, so the
      // failure has to land inside the dialog or the operator sees a Confirm
      // click that did nothing.
      setSaveError(error instanceof Error ? error.message : t('holdBack.unknownError'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{t('holdBack.confirmTitle')}</DialogTitle>
          <DialogDescription>{t('holdBack.confirmDesc')}</DialogDescription>
        </DialogHeader>

        {/* ONE stable live region, mounted for the life of the dialog. A
            role="status" inside the branch below is inserted together with its
            text, which several screen readers never announce — so Retry to a
            result would land silently. The failure branch owns its own
            role="alert", so this stays empty there rather than double-reading. */}
        <p className="sr-only" role="status">
          {previewLoading
            ? t('holdBack.calculating')
            : preview.isSuccess
              ? t('holdBack.previewReady', {
                  units: impact?.withheldUnits ?? 0,
                  skus: skusToZero,
                })
              : ''}
        </p>

        {previewLoading ? (
          <div
            className="flex items-center gap-2 py-6 text-sm text-muted-foreground"
            aria-hidden="true"
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('holdBack.calculating')}
          </div>
        ) : previewFailed ? (
          <div
            className="flex gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive"
            role="alert"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <div className="space-y-2">
              <p className="font-medium">{t('holdBack.previewFailedTitle')}</p>
              <p>{t('holdBack.previewFailedDesc')}</p>
              {/* The server's own reason. Without it a permission refusal, a
                  timeout and a bad location all render as the same sentence,
                  and Retry is the only thing left to try. */}
              {preview.error?.message && (
                <p className="break-words text-destructive/80">{preview.error.message}</p>
              )}
              <Button
                variant="outline"
                className="h-7 gap-1.5 px-2.5 text-xs"
                onClick={() => runPreview({ cells: cellsRef.current })}
              >
                {t('holdBack.retry')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-center sm:grid-cols-4">
              <Stat
                label={t('holdBack.statCells')}
                value={cells.length}
                detail={t('holdBack.statCellsSplit', cellsByProductionType)}
              />
              {/* Clamped server-side: a 30-unit hold-back on a SKU holding 4
                  withholds 4, not 30. An unclamped figure overstates badly. */}
              <Stat label={t('holdBack.statUnits')} value={impact?.withheldUnits ?? 0} />
              <Stat label={t('holdBack.statToZero')} value={skusToZero} emphasise={skusToZero > 0} />
              {/* Rows already at zero before this change: they are not in the
                  table below, and without them the three other figures look
                  like they don't add up. */}
              <Stat label={t('holdBack.statAlreadyZero')} value={impact?.alreadyZero ?? 0} />
            </div>

            {skusToZero > 0 && (
              <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <p>{t('holdBack.offSaleWarning', { count: skusToZero })}</p>
              </div>
            )}

            {impactRows.length > 0 && (
              <div className="space-y-1.5">
                <p id="hold-back-impact-heading" className="text-[11px] font-medium text-muted-foreground">
                  {t('holdBack.impactTableCaption')}
                </p>
                <div className="rounded-md border">
                  <div className="max-h-48 overflow-y-auto">
                    <table className="w-full text-xs" aria-labelledby="hold-back-impact-heading">
                      {/* Visually redundant (the columns read themselves), but a
                          screen reader announcing "MZ-TEE-BLK-L, 4 to 0" needs
                          the header row to make sense of the third column. */}
                      <thead className="sr-only">
                        <tr>
                          <th scope="col">{t('holdBack.impactColSku')}</th>
                          <th scope="col">{t('holdBack.impactColLocation')}</th>
                          <th scope="col">{t('holdBack.impactColChange')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {impactRows.map((r) => (
                          <tr key={`${r.sku}-${r.locationId}`} className="border-b last:border-0">
                            <td className="px-3 py-1.5 font-medium">{r.sku}</td>
                            <td className="px-3 py-1.5 text-muted-foreground">
                              {locationName(r.locationId)}
                            </td>
                            <td className="px-3 py-1.5 text-right tabular-nums">
                              {r.previousQty} → {r.newQty}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {impact?.truncated && (
                    <p className="border-t px-3 py-1.5 text-[11px] text-muted-foreground">
                      {t('holdBack.andMore', {
                        count: Math.max(skusToZero - impactRows.length, 0),
                      })}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* The change lands on the NEXT push cycle, not now. Without this
                line the grid reads as if it is already live. */}
            <p className="text-xs text-muted-foreground">{t('holdBack.nextCycleNote')}</p>
          </div>
        )}

        {saveError && (
          <div className="text-xs text-destructive" role="alert">
            <p className="font-medium">{t('holdBack.saveFailedTitle')}</p>
            <p>{saveError}</p>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            className="h-8 gap-1.5 px-3 text-xs"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            {t('holdBack.cancel')}
          </Button>
          {/* `default`, not `destructive`: this is reversible configuration, not
              a permanent delete. The amber off-sale banner + the impact table
              carry the weight (DESIGN §3.4). */}
          <Button
            className="h-8 gap-1.5 px-3 text-xs"
            onClick={() => void handleConfirm()}
            disabled={confirmDisabled}
          >
            {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
            {t('holdBack.confirmCta')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({
  label,
  value,
  detail,
  emphasise = false,
}: {
  label: string;
  value: number;
  /** One quieter line under the label, for a breakdown of the headline figure. */
  detail?: ReactNode;
  emphasise?: boolean;
}) {
  return (
    <div className="rounded-md border p-2">
      <div
        className={`text-lg font-semibold tabular-nums ${emphasise ? 'text-amber-600' : ''}`}
      >
        {value}
      </div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
      {detail && <div className="text-[10px] tabular-nums text-muted-foreground">{detail}</div>}
    </div>
  );
}
